/**
 * 🌐 WebSocket数据管理器
 *
 * 负责管理实时WebSocket RSM数据流
 * 支持连接管理、消息缓存、重连机制
 */

import { RSMMessage, VehicleData, RSMDataAdapter } from './RSMDataAdapter';

// === 配置接口定义 ===

/**
 * WebSocket连接配置
 */
export interface WebSocketConfig {
  /** WebSocket服务器地址 */
  url: string;
  /** 连接协议 */
  protocols?: string | string[];
  /** 重连配置 */
  reconnect?: {
    /** 是否启用自动重连 */
    enabled: boolean;
    /** 最大重连次数 */
    maxAttempts: number;
    /** 重连间隔（毫秒） */
    interval: number;
    /** 重连间隔递增因子 */
    backoffFactor?: number;
  };
  /** 缓冲区配置 */
  buffer?: {
    /** 缓冲区大小 */
    maxSize: number;
    /** 是否启用暂停缓存 */
    enablePauseBuffer: boolean;
  };
  /** 消息过滤器 */
  filters?: {
    /** RSU过滤列表，空表示接收所有 */
    rsuIds?: string[];
    /** 参与者类型过滤 */
    ptcTypes?: number[];
    /** 最小更新间隔（毫秒） */
    minUpdateInterval?: number;
  };
  /** 连接超时设置 */
  timeout?: {
    /** 连接超时（毫秒） */
    connect: number;
    /** 消息超时（毫秒） */
    message: number;
  };
  /** 认证配置 */
  auth?: {
    /** 认证token */
    token?: string;
    /** 认证类型 */
    type?: 'bearer' | 'basic' | 'custom';
    /** 自定义认证头 */
    headers?: Record<string, string>;
  };
}

/**
 * 连接状态
 */
export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error'
  | 'closed';

/**
 * WebSocket消息格式
 */
export interface WebSocketMessage {
  /** 消息类型 */
  type: 'rsm' | 'heartbeat' | 'auth' | 'error' | 'control';
  /** 消息数据 */
  data: any;
  /** 时间戳 */
  timestamp: number;
  /** 消息ID */
  messageId?: string;
}

/**
 * 连接统计信息
 */
export interface WebSocketStatistics {
  /** 接收消息数量 */
  messagesReceived: number;
  /** 处理消息数量 */
  messagesProcessed: number;
  /** 接收字节数 */
  bytesReceived: number;
  /** 连接时间 */
  connectionTime: number;
  /** 最后消息时间 */
  lastMessageTime: number;
  /** 重连次数 */
  reconnectCount: number;
  /** 错误次数 */
  errorCount: number;
}

// === 事件类型定义 ===

/**
 * WebSocket管理器事件
 */
export interface WebSocketManagerEvents {
  /** 连接状态变化 */
  'connection:statusChange': (status: ConnectionStatus, error?: Error) => void;
  /** 连接成功 */
  'connection:connected': () => void;
  /** 连接断开 */
  'connection:disconnected': (error?: Error) => void;
  /** 重连开始 */
  'connection:reconnecting': (attempt: number, maxAttempts: number) => void;
  /** 连接错误 */
  'connection:error': (error: Error) => void;

  /** 接收到RSM消息 */
  'message:rsm': (rsmMessage: RSMMessage, vehicles: VehicleData[]) => void;
  /** 接收到原始消息 */
  'message:raw': (message: WebSocketMessage) => void;
  /** 消息错误 */
  'message:error': (error: Error, rawMessage?: any) => void;

  /** 缓冲区状态变化 */
  'buffer:statusChange': (isPaused: boolean, bufferSize: number) => void;
  /** 缓冲区溢出 */
  'buffer:overflow': (droppedMessages: number) => void;
}

// === WebSocket数据管理器类 ===

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private connectionState: ConnectionStatus = 'disconnected';
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts: number = 0;
  private messageBuffer: RSMMessage[] = [];
  private isPaused: boolean = false;
  private statistics: WebSocketStatistics = {
    messagesReceived: 0,
    messagesProcessed: 0,
    bytesReceived: 0,
    connectionTime: 0,
    lastMessageTime: 0,
    reconnectCount: 0,
    errorCount: 0,
  };
  private lastMessageTimestamp = 0;

  // 事件监听
  private eventListeners: Map<string, Set<Function>> = new Map();

  constructor(config: WebSocketConfig) {
    // 默认配置
    this.config = {
      url: config.url,
      protocols: config.protocols || [],
      reconnect: {
        enabled: config.reconnect?.enabled ?? true,
        maxAttempts: config.reconnect?.maxAttempts ?? 5,
        interval: config.reconnect?.interval ?? 3000,
        backoffFactor: config.reconnect?.backoffFactor ?? 1.5,
      },
      buffer: {
        maxSize: config.buffer?.maxSize ?? 1000,
        enablePauseBuffer: config.buffer?.enablePauseBuffer ?? true,
      },
      filters: {
        rsuIds: config.filters?.rsuIds ?? [],
        ptcTypes: config.filters?.ptcTypes ?? [],
        minUpdateInterval: config.filters?.minUpdateInterval ?? 100,
      },
      timeout: {
        connect: config.timeout?.connect ?? 10000,
        message: config.timeout?.message ?? 5000,
      },
      auth: {
        token: config.auth?.token,
        type: config.auth?.type ?? 'bearer',
        headers: config.auth?.headers ?? {},
      },
    };

    this.initializeEventListeners();
  }

  /**
   * 初始化事件监听器
   */
  private initializeEventListeners(): void {
    const events: Array<keyof WebSocketManagerEvents> = [
      'connection:statusChange',
      'connection:connected',
      'connection:disconnected',
      'connection:reconnecting',
      'connection:error',
      'message:rsm',
      'message:raw',
      'message:error',
      'buffer:statusChange',
      'buffer:overflow',
    ];

    events.forEach(event => {
      this.eventListeners.set(event, new Set());
    });
  }

  /**
   * 连接到WebSocket服务器
   */
  async connect(): Promise<void> {
    if (!this.config.url) {
      throw new Error('WebSocket URL不能为空');
    }

    if (
      this.connectionState === 'connecting' ||
      this.connectionState === 'connected'
    ) {
      console.warn('[WebSocketManager] 已经在连接或已连接状态');
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        this.setStatus('connecting');

        // 创建WebSocket连接
        this.ws = new WebSocket(this.config.url, this.config.protocols);

        // 连接超时处理
        const connectTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            reject(new Error('连接超时'));
          }
        }, this.config.timeout.connect);

        // 连接成功
        this.ws.onopen = () => {
          clearTimeout(connectTimeout);
          this.handleConnectionOpen();
          resolve();
        };

        // 消息接收
        this.ws.onmessage = event => {
          this.handleMessage(event);
        };

        // 连接错误
        this.ws.onerror = () => {
          clearTimeout(connectTimeout);
          const error = new Error('WebSocket连接错误');
          this.handleConnectionError(error);
          reject(error);
        };

        // 连接关闭
        this.ws.onclose = event => {
          clearTimeout(connectTimeout);
          this.handleConnectionClose(event);
        };
      } catch (error) {
        this.setStatus('error');
        reject(error);
      }
    });
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, '手动断开连接');
      this.ws = null;
    }

    this.setStatus('disconnected');
    this.reconnectAttempts = 0;
  }

  /**
   * 发送消息
   */
  send(message: WebSocketMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocketManager] WebSocket未连接，无法发送消息');
      return false;
    }

    try {
      const messageStr = JSON.stringify(message);
      this.ws.send(messageStr);
      return true;
    } catch (error) {
      console.error('[WebSocketManager] 发送消息失败:', error);
      return false;
    }
  }

  /**
   * 发送认证消息
   */
  authenticate(): boolean {
    if (!this.config.auth.token) {
      return true; // 无需认证
    }

    const authMessage: WebSocketMessage = {
      type: 'auth',
      data: {
        token: this.config.auth.token,
        type: this.config.auth.type,
        headers: this.config.auth.headers,
      },
      timestamp: Date.now(),
    };

    return this.send(authMessage);
  }

  /**
   * 发送心跳
   */
  sendHeartbeat(): boolean {
    const heartbeatMessage: WebSocketMessage = {
      type: 'heartbeat',
      data: { timestamp: Date.now() },
      timestamp: Date.now(),
    };

    return this.send(heartbeatMessage);
  }

  /**
   * 暂停数据处理（开始缓存）
   */
  pause(): void {
    if (!this.isPaused) {
      this.isPaused = true;
      console.log('[WebSocketManager] 暂停数据处理，开始缓存消息');
      this.emit('buffer:statusChange', true, this.messageBuffer.length);
    }
  }

  /**
   * 恢复数据处理
   */
  resume(): void {
    if (this.isPaused) {
      this.isPaused = false;
      console.log(
        `[WebSocketManager] 恢复数据处理，缓存消息数量: ${this.messageBuffer.length}`
      );

      // 处理缓存的消息
      const bufferedMessages = [...this.messageBuffer];
      this.messageBuffer = [];

      bufferedMessages.forEach(message => {
        this.processRSMMessage(message);
      });

      this.emit('buffer:statusChange', false, 0);
    }
  }

  /**
   * 获取缓存的消息
   */
  getBufferedMessages(): RSMMessage[] {
    return [...this.messageBuffer];
  }

  /**
   * 清空消息缓存
   */
  clearBuffer(): void {
    const clearedCount = this.messageBuffer.length;
    this.messageBuffer = [];
    console.log(`[WebSocketManager] 清空消息缓存，清空数量: ${clearedCount}`);
    this.emit('buffer:statusChange', this.isPaused, 0);
  }

  /**
   * 跳转到最新数据
   */
  jumpToLatest(): void {
    if (this.isPaused) {
      // 只保留最新的几条消息
      const latestMessages = this.messageBuffer.slice(-10);
      this.messageBuffer = latestMessages;
      this.resume();
    }
  }

  /**
   * 获取连接状态
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionState;
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return (
      this.connectionState === 'connected' &&
      this.ws?.readyState === WebSocket.OPEN
    );
  }

  /**
   * 获取连接统计信息
   */
  getStatistics(): WebSocketStatistics {
    return { ...this.statistics };
  }

  /**
   * 手动重连
   */
  async reconnect(): Promise<void> {
    this.disconnect();
    await new Promise(resolve => setTimeout(resolve, 1000));
    return this.connect();
  }

  /**
   * 处理连接打开
   */
  private handleConnectionOpen(): void {
    console.log('[WebSocketManager] WebSocket连接成功');

    this.setStatus('connected');
    this.reconnectAttempts = 0;
    this.statistics.connectionTime = Date.now();

    // 发送认证
    this.authenticate();

    // 开始心跳
    this.startHeartbeat();

    this.emit('connection:connected');
  }

  /**
   * 处理消息接收
   */
  private handleMessage(event: MessageEvent): void {
    const now = Date.now();
    const delay =
      this.lastMessageTimestamp > 0 ? now - this.lastMessageTimestamp : 0;
    this.lastMessageTimestamp = now;
    console.log(
      `[WebSocketManager] 收到消息，时间: ${new Date(
        now
      ).toISOString()}, 与上一条消息间隔: ${delay}ms`
    );
    try {
      this.statistics.messagesReceived++;
      this.statistics.bytesReceived += event.data.length;

      // 首先尝试解析JSON消息
      let parsedMessage: any;
      try {
        parsedMessage = JSON.parse(event.data);
      } catch (parseError) {
        console.warn(
          '[WebSocketManager] 无法解析JSON消息，将作为文本处理:',
          event.data
        );
        this.emit('message:error', new Error('JSON解析失败'), event.data);
        return;
      }

      // 添加时间戳
      if (!parsedMessage.timestamp) {
        parsedMessage.timestamp = Date.now();
      }

      // 发送原始消息事件
      this.emit('message:raw', parsedMessage);

      // 处理各种可能的消息格式
      this.processAnyMessage(parsedMessage);
    } catch (error) {
      console.error('[WebSocketManager] 消息处理失败:', error);
      this.emit('message:error', error as Error, event.data);
    }
  }

  /**
   * 处理任何格式的消息 - 极简版本，确保所有消息都能被处理
   */
  private processAnyMessage(message: any): void {
    try {
      console.log('[WebSocketManager] 🚀 开始处理消息:', {
        type: typeof message,
        keys: Object.keys(message),
        messageType: message.type || message.msgType || 'unknown',
      });

      // 无论什么格式，都先尝试作为RSM消息处理
      this.attemptRSMProcessing(message);

      // 对于心跳消息的特殊处理
      if (message.type === 'heartbeat') {
        console.log('[WebSocketManager] 💓 处理心跳响应');
        this.handleHeartbeatResponse(message);
      }
    } catch (error) {
      console.error('[WebSocketManager] 处理消息时发生错误:', error);
      this.emit('message:error', error as Error, message);
    }
  }

  /**
   * 尝试将任何消息作为RSM消息处理
   */
  private attemptRSMProcessing(message: any): void {
    try {
      let rsmMessage: RSMMessage | null = null;

      // 方式1: 标准WebSocket消息格式 {type: 'rsm', data: ...}
      if (message.type === 'rsm' && message.data) {
        console.log('[WebSocketManager] 📦 检测到标准WebSocket RSM格式');
        rsmMessage = message.data as RSMMessage;
      }

      // 方式2: 直接RSM消息格式 {msgType: 'rsm', ...}
      else if (message.msgType === 'rsm') {
        console.log('[WebSocketManager] 📋 检测到直接RSM格式');
        rsmMessage = message as RSMMessage;
      }

      // 方式3: 包含参与者的任意格式
      else if (message.participants && Array.isArray(message.participants)) {
        console.log(
          '[WebSocketManager] 👥 检测到包含参与者的格式，尝试构造RSM'
        );
        rsmMessage = {
          msgType: 'rsm',
          rsuId: message.rsuId || message.id || message.source || 'UNKNOWN_RSU',
          timestamp: message.timestamp || message.time || Date.now(),
          uuid: message.uuid || message.messageId || `rsm_${Date.now()}`,
          participants: message.participants,
        };
      }

      // 方式4: 尝试检测任何可能的车辆数据格式
      else if (this.looksLikeVehicleData(message)) {
        console.log('[WebSocketManager] 🚗 检测到疑似车辆数据，尝试构造RSM');
        rsmMessage = this.constructRSMFromVehicleData(message);
      }

      // 如果成功识别为RSM消息，则处理
      if (rsmMessage) {
        console.log('[WebSocketManager] ✅ 成功识别RSM消息，开始处理');
        this.handleRSMMessage(rsmMessage);
      } else {
        console.log('[WebSocketManager] ❓ 无法识别为RSM格式，记录为通用消息');
        // 即使不是RSM格式，也发送raw事件让上层处理
        this.statistics.messagesProcessed++;
        this.statistics.lastMessageTime = Date.now();
        this.emit('message:raw', message);
      }
    } catch (error) {
      console.error('[WebSocketManager] RSM处理尝试失败:', error);
      // 发送错误但继续处理
      this.emit('message:error', error as Error, message);
    }
  }

  /**
   * 检测是否看起来像车辆数据
   */
  private looksLikeVehicleData(message: any): boolean {
    // 检查是否有位置相关字段
    const hasLocationFields =
      (message.latitude && message.longitude) ||
      (message.lat && message.lng) ||
      (message.position && typeof message.position === 'object') ||
      (message.location && typeof message.location === 'object');

    // 检查是否有车辆相关字段
    const hasVehicleFields =
      message.speed !== undefined ||
      message.heading !== undefined ||
      message.vehicleId !== undefined ||
      message.ptcId !== undefined ||
      message.ptcType !== undefined;

    return hasLocationFields || hasVehicleFields;
  }

  /**
   * 从疑似车辆数据构造RSM消息
   */
  private constructRSMFromVehicleData(message: any): RSMMessage {
    // 构造参与者对象
    const participant: any = {
      ptcId: message.ptcId || message.vehicleId || message.id || 1,
      ptcType: message.ptcType || message.vehicleType || 1, // 默认为机动车
      sourceType: message.sourceType || 2, // 默认为V2X
      ptcTimestamp: message.ptcTimestamp || message.timestamp || Date.now(),
      longitude: message.longitude || message.lng || message.position?.x || 0,
      latitude: message.latitude || message.lat || message.position?.y || 0,
      speed: message.speed || 0,
      heading: message.heading || message.direction || 0,
      width: message.width || 180, // 默认1.8m
      length: message.length || 450, // 默认4.5m
    };

    // 添加可选字段
    if (message.elevation || message.position?.z) {
      participant.elevation = message.elevation || message.position.z;
    }
    if (message.plateNo) {
      participant.plateNo = message.plateNo;
    }

    return {
      msgType: 'rsm',
      rsuId: message.rsuId || message.source || 'CONSTRUCTED_RSU',
      timestamp: message.timestamp || Date.now(),
      uuid: message.uuid || `constructed_${Date.now()}`,
      participants: [participant],
    };
  }

  /**
   * 统一的RSM消息处理方法
   */
  private handleRSMMessage(rsmMessage: RSMMessage): void {
    try {
      if (this.isPaused && this.config.buffer.enablePauseBuffer) {
        this.bufferMessage(rsmMessage);
      } else {
        this.processRSMMessage(rsmMessage);
      }
    } catch (error) {
      console.error('[WebSocketManager] RSM消息处理失败:', error);
      this.emit('message:error', error as Error, rsmMessage);
    }
  }

  /**
   * 处理连接错误
   */
  private handleConnectionError(error: Error): void {
    console.error('[WebSocketManager] WebSocket连接错误:', error);
    this.setStatus('error');
    this.statistics.errorCount++;
    this.emit('connection:error', error);
  }

  /**
   * 处理连接关闭
   */
  private handleConnectionClose(event: CloseEvent): void {
    console.log(
      `[WebSocketManager] WebSocket连接关闭: ${event.code} - ${event.reason}`
    );

    const error =
      event.code !== 1000 ? new Error(`连接关闭: ${event.reason}`) : undefined;

    this.setStatus('disconnected');
    this.emit('connection:disconnected', error);

    // 自动重连
    if (this.config.reconnect.enabled && event.code !== 1000) {
      this.scheduleReconnect();
    }
  }

  /**
   * 安排重连
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.reconnect.maxAttempts) {
      console.error('[WebSocketManager] 达到最大重连次数，停止重连');
      return;
    }

    this.reconnectAttempts++;
    this.statistics.reconnectCount++;

    const delay =
      this.config.reconnect.interval *
      Math.pow(
        this.config.reconnect.backoffFactor ?? 1.5,
        this.reconnectAttempts - 1
      );

    console.log(
      `[WebSocketManager] 将在 ${delay}ms 后进行第 ${this.reconnectAttempts} 次重连`
    );
    this.setStatus('reconnecting');
    this.emit(
      'connection:reconnecting',
      this.reconnectAttempts,
      this.config.reconnect.maxAttempts
    );

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.connect();
      } catch (error) {
        console.error('[WebSocketManager] 重连失败:', error);
        this.scheduleReconnect();
      }
    }, delay);
  }

  /**
   * 设置连接状态
   */
  private setStatus(status: ConnectionStatus): void {
    if (this.connectionState !== status) {
      const previousStatus = this.connectionState;
      this.connectionState = status;
      console.log(
        `[WebSocketManager] 状态变化: ${previousStatus} -> ${status}`
      );
      this.emit('connection:statusChange', status);
    }
  }

  /**
   * 缓存消息
   */
  private bufferMessage(rsmMessage: RSMMessage): void {
    this.messageBuffer.push(rsmMessage);

    // 检查缓冲区溢出
    if (this.messageBuffer.length > this.config.buffer.maxSize) {
      const droppedCount =
        this.messageBuffer.length - this.config.buffer.maxSize;
      this.messageBuffer = this.messageBuffer.slice(
        -this.config.buffer.maxSize
      );
      this.emit('buffer:overflow', droppedCount);
    }

    this.emit('buffer:statusChange', this.isPaused, this.messageBuffer.length);
  }

  /**
   * 处理RSM消息
   */
  private processRSMMessage(rsmMessage: RSMMessage): void {
    try {
      // 先记录消息统计
      this.statistics.messagesProcessed++;
      this.statistics.lastMessageTime = Date.now();

      // 调试日志
      console.log('[WebSocketManager] 处理RSM消息:', {
        rsuId: rsmMessage.rsuId,
        participantCount: rsmMessage.participants?.length || 0,
        timestamp: rsmMessage.timestamp,
      });

      // 应用过滤器（仅在配置了过滤器时才过滤）
      if (!this.shouldProcessRSMMessage(rsmMessage)) {
        console.log('[WebSocketManager] RSM消息被过滤器排除');
        return;
      }

      // 尝试转换为车辆数据
      let vehicleData: VehicleData[] = [];
      try {
        vehicleData = RSMDataAdapter.convertRSMMessage(rsmMessage);
        console.log(
          '[WebSocketManager] 成功转换车辆数据:',
          vehicleData.length,
          '辆车',
          JSON.stringify(vehicleData)
        );
      } catch (convertError) {
        console.error('[WebSocketManager] 车辆数据转换失败:', convertError);
        // 即使转换失败，也发送原始RSM消息
        vehicleData = [];
      }

      // 发送RSM消息事件（不管转换是否成功都发送）
      this.emit('message:rsm', rsmMessage, vehicleData);
    } catch (error) {
      console.error('[WebSocketManager] RSM消息处理失败:', error);
      this.emit('message:error', error as Error, rsmMessage);
    }
  }

  /**
   * 检查是否应该处理RSM消息 - 极简版本，基本不过滤
   */
  private shouldProcessRSMMessage(rsmMessage: RSMMessage): boolean {
    console.log('[WebSocketManager] 🔍 检查是否处理RSM消息');

    // 为了确保能接收到所有消息，暂时禁用所有过滤
    // 只做最基本的有效性检查

    if (!rsmMessage) {
      console.log('[WebSocketManager] ❌ RSM消息为空');
      return false;
    }

    if (!rsmMessage.msgType || rsmMessage.msgType !== 'rsm') {
      console.log('[WebSocketManager] ⚠️ 消息类型不是RSM，但仍尝试处理');
      // 即使类型不对，也尝试处理
    }

    console.log('[WebSocketManager] ✅ RSM消息通过检查，准备处理');
    return true;

    // 注释掉原有的过滤逻辑，确保所有消息都能通过
    /*
    const filters = this.config.filters;

    // 如果没有配置过滤器，接受所有消息
    if (!filters) {
      return true;
    }

    // RSU过滤（只有明确配置了RSU ID列表才过滤）
    if (filters.rsuIds && filters.rsuIds.length > 0) {
      if (!rsmMessage.rsuId || !filters.rsuIds.includes(rsmMessage.rsuId)) {
        console.log(`[WebSocketManager] RSU ID过滤: ${rsmMessage.rsuId} 不在允许列表中`);
        return false;
      }
    }

    // 参与者类型过滤（只有明确配置了类型列表才过滤）
    if (filters.ptcTypes && filters.ptcTypes.length > 0) {
      if (!rsmMessage.participants || rsmMessage.participants.length === 0) {
        console.log('[WebSocketManager] 参与者类型过滤: 没有参与者数据');
        return false;
      }

      const hasMatchingType = rsmMessage.participants.some(participant =>
        filters.ptcTypes!.includes(participant.ptcType)
      );

      if (!hasMatchingType) {
        console.log('[WebSocketManager] 参与者类型过滤: 没有匹配的参与者类型');
        return false;
      }
    }

    return true;
    */
  }

  /**
   * 开始心跳
   */
  private startHeartbeat(): void {
    setInterval(() => {
      if (this.isConnected()) {
        this.sendHeartbeat();
      }
    }, 30000);
  }

  /**
   * 处理心跳响应
   */
  private handleHeartbeatResponse(_message: WebSocketMessage): void {
    // 心跳响应处理逻辑
    this.statistics.lastMessageTime = Date.now();
  }

  /**
   * 事件监听
   */
  on<K extends keyof WebSocketManagerEvents>(
    event: K,
    handler: WebSocketManagerEvents[K]
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.add(handler);
    }
  }

  /**
   * 移除事件监听
   */
  off<K extends keyof WebSocketManagerEvents>(
    event: K,
    handler: WebSocketManagerEvents[K]
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * 触发事件
   */
  private emit<K extends keyof WebSocketManagerEvents>(
    event: K,
    ...args: Parameters<WebSocketManagerEvents[K]>
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          (handler as any)(...args);
        } catch (error) {
          console.error(`[WebSocketManager] 事件处理器错误 (${event}):`, error);
        }
      });
    }
  }

  /**
   * 清理资源
   */
  dispose(): void {
    this.disconnect();
    this.messageBuffer = [];
    this.eventListeners.clear();
  }
}

// === 导出工厂函数 ===

/**
 * 创建WebSocket数据管理器
 */
export function createWebSocketManager(
  config: WebSocketConfig
): WebSocketManager {
  return new WebSocketManager(config);
}

// === 默认导出 ===

export default WebSocketManager;
